import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import FiltroSucursalVentaSelect from '../../components/shared/FiltroSucursalVentaSelect';
import { useSucursalesVentaFiltro } from '../../hooks/useSucursalesVentaFiltro';
import { useFechasVentaFiltro } from '../../hooks/useFechasVentaFiltro';
import SearchIcon from '@mui/icons-material/Search';
import SpeedIcon from '@mui/icons-material/Speed';
import { ROUTES } from '../../config/routes';
import PrintIcon from '@mui/icons-material/Print';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/material/CircularProgress';
import { useNavigate } from 'react-router-dom';
import { facturaPosPath, pickingMasivoPath } from '../../config/routes';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useSnackbar } from 'notistack';
import pedidoVentaService, { PedidoVenta, PedidosVentaKpis, emptyPedidosVentaKpis } from '../../services/pedidoVentaService';
import configuracionService from '../../services/configuracionService';
import {
  aplicarConversionMaestrosConFloor,
  pedidoTieneCantidadB,
  puedeConversionPedidoListado,
} from '../../services/pedidoVentaConversionMaestros';
import {
  getPedidoEstadoChip,
  PEDIDO_ESTADO_FILTER_OPTIONS,
  PEDIDO_ESTADOS,
  estadosPedidoCoinciden,
} from '../../utils/pedidoVentaEstados';
import PedidoDetalle from './components/PedidoDetalle';
import CopiarPedidoRelacionadoDialog from './components/CopiarPedidoRelacionadoDialog';
import PedidosKpisBar from './components/PedidosKpisBar';
import PDFViewer from '../../components/shared/PDFViewer';
import { formatCurrency, formatClienteConNro } from '../../utils/formatters';
import { formatFechaVencimDisplay } from '../../utils/facturaVentaFechas';
import { getCookie, setCookie } from '../../utils/cookies';
import { getApiErrorMessage } from '../../utils/apiError';
import Can from '../../components/Can';
import { useGridColumns, withColumnSx, type GridColumnDef } from '../../hooks/useGridColumns';
import { descubrirColumnasDeItems, presentarCampoGrilla } from '../../utils/gridFieldValue';
import {
  AccionesColumnHeader,
  PersonalizarColumnasDialog,
} from '../../components/shared/PersonalizarColumnasDialog';
import type { SxProps, Theme } from '@mui/material/styles';
import { useRegisterPageTour } from '../../components/tour/PageTourContext';
import { PEDIDOS_LISTADO_TOUR } from '../../config/tours/pedidosVentaTour';

type PedidoColumnId =
  | 'fecha'
  | 'vencimiento'
  | 'comprobante'
  | 'cliente'
  | 'importe'
  | 'estado'
  | 'acciones';

const PEDIDOS_COLUMNAS: GridColumnDef<PedidoColumnId>[] = [
  { id: 'fecha', label: 'Fecha' },
  { id: 'vencimiento', label: 'Vencimiento' },
  {
    id: 'comprobante',
    label: 'Comprobante',
    sx: { display: { xs: 'none', lg: 'table-cell' }, minWidth: 160, whiteSpace: 'nowrap' },
  },
  { id: 'cliente', label: 'Cliente' },
  { id: 'importe', label: 'Importe Total', align: 'right' },
  { id: 'estado', label: 'Estado' },
  { id: 'acciones', label: 'Acciones', align: 'center', alwaysVisible: true },
];

const Pedidos: React.FC = () => {
  useRegisterPageTour(PEDIDOS_LISTADO_TOUR);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { enqueueSnackbar } = useSnackbar();
  const { fechaDesde, setFechaDesde, fechaHasta, setFechaHasta } = useFechasVentaFiltro();

  const [items, setItems] = useState<PedidoVenta[]>([]);
  const [estadosActualizando, setEstadosActualizando] = useState<Set<number>>(() => new Set());
  const estadosActualizandoRef = useRef(new Set<number>());
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState<string>('');
  const { filtroNroSuc, setFiltroNroSuc, sucursales, loadingSucursales, filtroListo } = useSucursalesVentaFiltro();
  const [filtroPendientesFacturar, setFiltroPendientesFacturar] = useState<boolean>(() => {
    const cookieValue = getCookie('pedidos_filtro_pendientes_facturar');
    return cookieValue === 'true';
  });
  const [kpis, setKpis] = useState<PedidosVentaKpis>(emptyPedidosVentaKpis);
  const [kpisVersion, setKpisVersion] = useState(0);
  const [detalleDialogOpen, setDetalleDialogOpen] = useState(false);
  const [selectedPedidoId, setSelectedPedidoId] = useState<number | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [selectedPedido, setSelectedPedido] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<number | null>(null);
  const [kebabAnchor, setKebabAnchor] = useState<{ el: HTMLElement; pedidoId: number } | null>(null);
  const [conversionHabilitada, setConversionHabilitada] = useState(false);
  const [conversionAutoLoadingId, setConversionAutoLoadingId] = useState<number | null>(null);
  /** Cache: pedidoId → tiene Cantidad B (undefined = aún no consultado). */
  const [cantidadBPorPedido, setCantidadBPorPedido] = useState<Record<number, boolean>>({});
  const [cantidadBLoadingId, setCantidadBLoadingId] = useState<number | null>(null);
  const [enviarPedidoId, setEnviarPedidoId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    void configuracionService.isConversionPedidoCantBHabilitada().then((ok) => {
      if (!cancelled) setConversionHabilitada(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    columnasVisibles,
    columnasAnchorEl,
    setColumnasAnchorEl,
    gridColumns,
    toggleColumnaVisible,
    moverColumna,
    reordenarColumnas,
    agregarColumna,
    restablecerColumnas,
    columnasDefinidas,
  } = useGridColumns({
    gridKey: 'pedidos',
    columns: PEDIDOS_COLUMNAS,
  });
  const columnasParaAgregar = useMemo(
    () => descubrirColumnasDeItems(items, gridColumns.columnOrder),
    [gridColumns.columnOrder, items],
  );
  const columnasTabla = columnasVisibles;

  const fetchPedidos = useCallback(async (
    pageNumber: number,
    pageSize: number,
    search: string,
    estado: string,
    desde: string,
    hasta: string,
    pendientesFacturar: boolean,
    nroSuc: number | null
  ) => {
    try {
      const validPageSize = Math.max(1, Math.min(20, pageSize));
      const params: Parameters<typeof pedidoVentaService.getPedidosVenta>[0] = {
        pageNumber: pageNumber + 1,
        pageSize: validPageSize,
        searchTerm: search || undefined,
        fechaDesde: desde || undefined,
        fechaHasta: hasta || undefined,
        PendienteFacturar: pendientesFacturar || undefined,
        nroSuc: nroSuc != null && nroSuc > 0 ? nroSuc : undefined,
      };
      if (estado) params.estado = estado;
      const response = await pedidoVentaService.getPedidosVenta(params);
      setItems(response.items);
      setTotalRecords(response.totalCount);
    } catch (error: any) {
      const message = getApiErrorMessage(error, 'Error al cargar los pedidos');
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
    }
  }, [enqueueSnackbar]);

  const fetchKpis = useCallback(async (
    search: string,
    estado: string,
    desde: string,
    hasta: string,
    pendientesFacturar: boolean,
    nroSuc: number | null
  ): Promise<PedidosVentaKpis> => {
    const params: Parameters<typeof pedidoVentaService.getPedidosVentaKpis>[0] = {
      searchTerm: search || undefined,
      fechaDesde: desde || undefined,
      fechaHasta: hasta || undefined,
      PendienteFacturar: pendientesFacturar || undefined,
      nroSuc: nroSuc != null && nroSuc > 0 ? nroSuc : undefined,
    };
    if (estado) params.estado = estado;
    return pedidoVentaService.getPedidosVentaKpis(params);
  }, []);

  useEffect(() => {
    if (!filtroListo) return;
    fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc);
  }, [page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc, filtroListo, fetchPedidos]);

  useEffect(() => {
    if (!filtroListo) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchKpis(
          searchTerm,
          filtroEstado,
          fechaDesde,
          fechaHasta,
          filtroPendientesFacturar,
          filtroNroSuc,
        );
        if (!cancelled) setKpis(result);
      } catch (error: any) {
        if (cancelled) return;
        setKpis(emptyPedidosVentaKpis());
        const message = getApiErrorMessage(error, 'Error al calcular los KPI de pedidos');
        enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc, filtroListo, fetchKpis, enqueueSnackbar, kpisVersion]);

  const refreshListado = useCallback(() => {
    fetchPedidos(page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc);
    setKpisVersion((v) => v + 1);
  }, [fetchPedidos, page, rowsPerPage, searchTerm, filtroEstado, fechaDesde, fechaHasta, filtroPendientesFacturar, filtroNroSuc]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const validRowsPerPage = Math.max(1, Math.min(20, parseInt(event.target.value, 10)));
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleNuevoPedido = () => navigate(ROUTES.ventas.pedidos.pos);

  const handleComprobanteClick = (pedido: PedidoVenta) => {
    if (pedido.estado === 'Nuevo') {
      navigate(`/ventas/pedidos/${pedido.nroFormulario}/editar`);
    } else {
      navigate(`/ventas/pedidos/${pedido.nroFormulario}/ver`);
    }
  };

  const handleCloseDetalle = () => {
    setDetalleDialogOpen(false);
    setSelectedPedidoId(null);
  };

  const handleKebabOpen = (event: React.MouseEvent<HTMLElement>, pedidoId: number) => {
    event.stopPropagation();
    if (estadosActualizandoRef.current.has(pedidoId)) return;
    setKebabAnchor({ el: event.currentTarget, pedidoId });
    const pedidoLista = items.find((p) => p.nroFormulario === pedidoId);
    if (
      conversionHabilitada &&
      pedidoLista &&
      puedeConversionPedidoListado(pedidoLista) &&
      cantidadBPorPedido[pedidoId] === undefined
    ) {
      setCantidadBLoadingId(pedidoId);
      void pedidoVentaService
        .getPedidoVenta(pedidoId, { sinCache: true })
        .then((full) => {
          setCantidadBPorPedido((prev) => ({
            ...prev,
            [pedidoId]: pedidoTieneCantidadB(full),
          }));
        })
        .catch(() => {
          setCantidadBPorPedido((prev) => ({ ...prev, [pedidoId]: false }));
        })
        .finally(() => {
          setCantidadBLoadingId((curr) => (curr === pedidoId ? null : curr));
        });
    }
  };

  const handleKebabClose = () => setKebabAnchor(null);

  const handleFacturar = (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    navigate(facturaPosPath({ pedidoId }));
  };

  const handleConversionAutomatica = async (pedidoId: number) => {
    handleKebabClose();
    try {
      setConversionAutoLoadingId(pedidoId);
      const { tieneCantidadB } = await aplicarConversionMaestrosConFloor(pedidoId);
      setCantidadBPorPedido((prev) => ({ ...prev, [pedidoId]: tieneCantidadB }));
      if (tieneCantidadB) {
        enqueueSnackbar('Conversión automática aplicada.', { variant: 'success' });
      } else {
        enqueueSnackbar(
          'No quedó Cantidad B (revisá % de maestros o redondeo hacia abajo).',
          { variant: 'warning', autoHideDuration: 6000 },
        );
      }
      refreshListado();
    } catch (error: unknown) {
      enqueueSnackbar(
        getApiErrorMessage(error, 'Error al aplicar la conversión automática'),
        { variant: 'error', autoHideDuration: 5000 },
      );
    } finally {
      setConversionAutoLoadingId(null);
    }
  };

  const handleAbrirEnviarPedido = (pedidoId: number) => {
    handleKebabClose();
    setEnviarPedidoId(pedidoId);
  };

  const cambiarEstadoPedido = async (
    pedidoId: number,
    estado: string,
    asentado: string | undefined,
    actualizar: () => Promise<void>,
    mensajeExito: string,
    mensajeError: string,
  ) => {
    if (estadosActualizandoRef.current.has(pedidoId)) return;

    const pedidoAnterior = items.find((pedido) => pedido.nroFormulario === pedidoId);
    estadosActualizandoRef.current.add(pedidoId);
    setEstadosActualizando(new Set(estadosActualizandoRef.current));
    setItems((prev) =>
      prev.map((pedido) =>
        pedido.nroFormulario === pedidoId
          ? { ...pedido, estado, ...(asentado ? { asentado } : {}) }
          : pedido,
      ),
    );

    try {
      await actualizar();
      enqueueSnackbar(mensajeExito, { variant: 'success' });
      const saleDelFiltro =
        (Boolean(filtroEstado) && !estadosPedidoCoinciden(estado, filtroEstado)) ||
        (filtroPendientesFacturar && estado === PEDIDO_ESTADOS.ANULADO);

      if (saleDelFiltro) {
        setItems((prev) => prev.filter((pedido) => pedido.nroFormulario !== pedidoId));
        setTotalRecords((prev) => Math.max(0, prev - 1));
        refreshListado();
      } else {
        setKpisVersion((version) => version + 1);
      }
    } catch (error: unknown) {
      if (pedidoAnterior) {
        setItems((prev) =>
          prev.map((pedido) =>
            pedido.nroFormulario === pedidoId
              ? { ...pedido, estado: pedidoAnterior.estado, asentado: pedidoAnterior.asentado }
              : pedido,
          ),
        );
      }
      enqueueSnackbar(getApiErrorMessage(error, mensajeError), {
        variant: 'error',
        autoHideDuration: 3000,
      });
    } finally {
      estadosActualizandoRef.current.delete(pedidoId);
      setEstadosActualizando(new Set(estadosActualizandoRef.current));
    }
  };

  const handleMarcarImpreso = async (pedidoId: number) => {
    handleKebabClose();
    await cambiarEstadoPedido(
      pedidoId,
      PEDIDO_ESTADOS.IMPRESO,
      'I',
      () => pedidoVentaService.marcarImpreso(pedidoId),
      'Pedido marcado como impreso',
      'Error al marcar el pedido como impreso',
    );
  };

  const handleMarcarListoPicking = async (pedidoId: number) => {
    handleKebabClose();
    await cambiarEstadoPedido(
      pedidoId,
      PEDIDO_ESTADOS.LISTO_PICKEAR,
      'K',
      () => pedidoVentaService.marcarListoPicking(pedidoId),
      'Pedido enviado a picking',
      'Error al enviar el pedido a picking',
    );
  };

  const handleMarcarPreparado = async (pedidoId: number) => {
    handleKebabClose();
    await cambiarEstadoPedido(
      pedidoId,
      PEDIDO_ESTADOS.PREPARADO,
      'P',
      () => pedidoVentaService.marcarPreparado(pedidoId),
      'Pedido marcado como preparado',
      'Error al marcar el pedido como preparado',
    );
  };

  const displayItems = items;
  const displayTotal = totalRecords;

  const handleAnular = async (pedidoId: number) => {
    handleKebabClose();
    if (window.confirm('¿Está seguro que desea anular este pedido?')) {
      await cambiarEstadoPedido(
        pedidoId,
        PEDIDO_ESTADOS.ANULADO,
        undefined,
        () => pedidoVentaService.anularPedido(pedidoId),
        'Pedido anulado correctamente',
        'Error al anular el pedido',
      );
    }
  };

  const handleImprimirPDF = async (pedidoId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      setPdfLoading(pedidoId);
      const blob = await pedidoVentaService.getPedidoVentaPDF(pedidoId);
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setSelectedPedido(pedidoId);
    } catch (error: any) {
      const message = error?.response?.data || 'Error al generar el PDF del pedido';
      enqueueSnackbar(message, { variant: 'error', autoHideDuration: 3000 });
    } finally {
      setPdfLoading(null);
    }
  };

  const handleClosePDF = () => {
    if (pdfUrl) window.URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setSelectedPedido(null);
  };

  const calcularTotal = (pedido: PedidoVenta): number => {
    if (pedido.importeTotal !== undefined && pedido.importeTotal !== null) {
      return pedido.importeTotal;
    }
    return pedido.detalles?.reduce((sum, d) => {
      const subtotal = d.subtotal || (d.precioUnitario || 0) * (d.cantidad || 0);
      return sum + subtotal;
    }, 0) || 0;
  };

  const puedeFacturar = (pedido: PedidoVenta): boolean =>
    pedido.estado !== 'Facturado' && pedido.estado !== 'Listo para pickear';

  const permiteAcciones = (pedido: PedidoVenta): boolean =>
    pedido.estado !== 'Facturado' && pedido.estado !== 'Anulado';

  const puedeImprimir = (pedido: PedidoVenta): boolean =>
    (permiteAcciones(pedido) && pedido.asentado !== 'P' && pedido.asentado !== 'K') ||
    pedido.estado === 'Facturado';

  const getEstadoChip = (pedido: PedidoVenta) => getPedidoEstadoChip(pedido.estado);
  const renderEstadoChip = (pedido: PedidoVenta) => {
    const estadoChip = getEstadoChip(pedido);
    const actualizando = estadosActualizando.has(pedido.nroFormulario);
    return (
      <Chip
        label={
          <Stack direction="row" spacing={0.75} alignItems="center">
            <span>{estadoChip.label}</span>
            {actualizando && (
              <CircularProgress
                size={12}
                color="inherit"
                aria-label="Actualizando estado"
              />
            )}
          </Stack>
        }
        size="small"
        sx={{
          bgcolor: estadoChip.bgColor,
          color: estadoChip.textColor,
          fontWeight: 500,
          borderRadius: 1.5,
          minWidth: 90,
        }}
      />
    );
  };

  const formatComprobante = (pedido: PedidoVenta) => {
    const letra = (pedido as any).letra;
    const nroSuc = (pedido as any).nroSuc;
    const nroComprobante = (pedido as any).nroComprobante;

    if (!letra || nroSuc === undefined || nroComprobante === undefined || nroSuc === null || nroComprobante === null) {
      return '-';
    }

    const formatPart = (value: number | string, length: number) => {
      if (value === null || value === undefined) return ''.padStart(length, '0');
      const numeric = Number(value);
      if (!Number.isNaN(numeric)) return numeric.toString().padStart(length, '0');
      return String(value).padStart(length, '0');
    };

    const letraNormalizada = letra.trim().toUpperCase();
    if (!letraNormalizada) return '-';
    return `${letraNormalizada}-${formatPart(nroSuc, 5)}-${formatPart(nroComprobante, 8)}`;
  };

  const btnSx = (bgColor: string, hoverColor: string) => ({
    bgcolor: bgColor,
    color: '#fff',
    borderRadius: 1.5,
    width: 32,
    height: 32,
    '&:hover': { bgcolor: hoverColor, transform: 'scale(1.1)' },
    '&:disabled': { bgcolor: '#ccc' },
    transition: 'all 0.2s'
  });

  const comprobanteSx = {
    cursor: 'pointer',
    color: 'primary.main',
    fontWeight: 500,
    '&:hover': { textDecoration: 'underline' }
  };

  const currentKebabPedido = kebabAnchor
    ? items.find((p) => p.nroFormulario === kebabAnchor.pedidoId)
    : null;

  const renderKebabItems = (pedido: PedidoVenta) => {
    const menuItems = [];
    if (pedido.asentado !== 'P' && pedido.asentado !== 'K' && pedido.asentado !== 'I') {
      menuItems.push(
        <MenuItem key="impreso" onClick={() => handleMarcarImpreso(pedido.nroFormulario)}>
          <ListItemIcon><InventoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Marcar como impreso</ListItemText>
        </MenuItem>
      );
    }
    if (pedido.asentado === 'K') {
      menuItems.push(
        <MenuItem key="pickear" onClick={() => navigate(pickingMasivoPath(pedido.nroFormulario))}>
          <ListItemIcon><QrCodeScannerIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Pickear pedido</ListItemText>
        </MenuItem>
      );
    }
    if (pedido.asentado !== 'P' && pedido.asentado !== 'K') {
      menuItems.push(
        <MenuItem key="picking" onClick={() => handleMarcarListoPicking(pedido.nroFormulario)}>
          <ListItemIcon><InventoryIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Enviar a picking</ListItemText>
        </MenuItem>
      );
    }
    if (pedido.estado !== 'Preparado') {
      menuItems.push(
        <MenuItem key="preparado" onClick={() => handleMarcarPreparado(pedido.nroFormulario)}>
          <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Marcar preparado</ListItemText>
        </MenuItem>
      );
    }

    if (conversionHabilitada && puedeConversionPedidoListado(pedido)) {
      const id = pedido.nroFormulario;
      const tieneB = cantidadBPorPedido[id] === true;
      const cargandoB = cantidadBLoadingId === id;
      const convirtiendo = conversionAutoLoadingId === id;

      menuItems.push(
        <MenuItem
          key="conversion-auto"
          onClick={() => void handleConversionAutomatica(id)}
          disabled={convirtiendo}
        >
          <ListItemIcon>
            {convirtiendo ? (
              <CircularProgress size={18} />
            ) : (
              <SyncAltIcon fontSize="small" />
            )}
          </ListItemIcon>
          <ListItemText>
            {convirtiendo ? 'Convirtiendo…' : 'Conversión automática'}
          </ListItemText>
        </MenuItem>,
      );

      menuItems.push(
        <Tooltip
          key="enviar-pedido-tip"
          title={
            cargandoB
              ? 'Verificando Cantidad B…'
              : tieneB
                ? ''
                : 'Primero aplicá Conversión automática (o generá Cantidad B en el pedido)'
          }
        >
          <span>
            <MenuItem
              onClick={() => handleAbrirEnviarPedido(id)}
              disabled={!tieneB || cargandoB || convirtiendo}
            >
              <ListItemIcon>
                {cargandoB ? <CircularProgress size={18} /> : <SendIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText>Enviar pedido</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>,
      );
    }

    menuItems.push(
      <Can key="anular" permiso="ventas.pedidos.anular">
        <MenuItem onClick={() => handleAnular(pedido.nroFormulario)} sx={{ color: 'error.main' }}>
          <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
          <ListItemText>Anular pedido</ListItemText>
        </MenuItem>
      </Can>
    );
    return menuItems;
  };

  const renderCeldaPedido = (columnId: string, pedido: PedidoVenta, colSx: SxProps<Theme> | undefined) => {
    switch (columnId) {
      case 'fecha':
        return <TableCell key={columnId} sx={colSx}>{new Date(pedido.fechaEmision).toLocaleDateString()}</TableCell>;
      case 'vencimiento':
        return (
          <TableCell key={columnId} sx={colSx}>
            {formatFechaVencimDisplay(pedido.fechaVencim ?? pedido.fechaEmision)}
          </TableCell>
        );
      case 'comprobante':
        return (
          <TableCell key={columnId} sx={colSx}>
            <Typography
              component="span"
              sx={comprobanteSx}
              onClick={() => handleComprobanteClick(pedido)}
            >
              {formatComprobante(pedido)}
            </Typography>
          </TableCell>
        );
      case 'cliente':
        return (
          <TableCell key={columnId} sx={withColumnSx({ fontWeight: 600 }, colSx)}>
            {formatClienteConNro(pedido.nroCliente, pedido.nombre)}
          </TableCell>
        );
      case 'importe':
        return (
          <TableCell key={columnId} align="right" sx={withColumnSx({ fontWeight: 600, color: '#1976d2' }, colSx)}>
            {formatCurrency(calcularTotal(pedido))}
          </TableCell>
        );
      case 'estado': {
        return (
          <TableCell key={columnId} sx={colSx}>
            {renderEstadoChip(pedido)}
          </TableCell>
        );
      }
      case 'acciones':
        return (
          <TableCell key={columnId} align="center" sx={colSx}>
            <Stack
              direction="row"
              spacing={0.5}
              justifyContent="center"
              {...(pedido.nroFormulario === displayItems[0]?.nroFormulario
                ? { 'data-tour-id': 'pedidos-acciones' }
                : {})}
            >
              {puedeImprimir(pedido) && (
                <Can permiso="ventas.pedidos.imprimir">
                  <Tooltip title="Imprimir PDF">
                    <IconButton
                      size="small"
                      onClick={(e) => handleImprimirPDF(pedido.nroFormulario, e)}
                      disabled={pdfLoading === pedido.nroFormulario}
                      sx={btnSx('#2196f3', '#1976d2')}
                    >
                      {pdfLoading === pedido.nroFormulario
                        ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                        : <PrintIcon fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Can>
              )}
              {puedeFacturar(pedido) && permiteAcciones(pedido) && (
                <Can permiso="ventas.facturas.nuevo">
                  <Tooltip title="Facturar pedido">
                    <IconButton
                      size="small"
                      onClick={(e) => handleFacturar(pedido.nroFormulario, e)}
                      disabled={estadosActualizando.has(pedido.nroFormulario)}
                      sx={btnSx('#4caf50', '#45a049')}
                    >
                      <ReceiptIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Can>
              )}
              {permiteAcciones(pedido) && (
                <Tooltip title="Más acciones">
                  <IconButton
                    size="small"
                    onClick={(e) => handleKebabOpen(e, pedido.nroFormulario)}
                    disabled={estadosActualizando.has(pedido.nroFormulario)}
                    sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </TableCell>
        );
      default: {
        const campo = presentarCampoGrilla(pedido, columnId);
        return (
          <TableCell key={columnId} align={campo.align} sx={colSx}>
            {campo.texto}
          </TableCell>
        );
      }
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
        <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976d2' }}>
                Pedidos
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    <Can permiso="ventas.pedidos.nuevo">
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SpeedIcon />}
                      onClick={handleNuevoPedido}
                      data-tour-id="pedidos-nueva-pos"
                      sx={{
                        borderRadius: 2,
                        px: 2.5,
                        py: 1,
                        textTransform: 'none',
                        fontWeight: 500,
                        boxShadow: '0 2px 4px rgba(25, 118, 210, 0.3)',
                        '&:hover': { boxShadow: '0 4px 8px rgba(25, 118, 210, 0.4)' },
                      }}
                    >
                      Nuevo Pedido POS
                    </Button>
                    </Can>
              </Stack>
            </Stack>

            {/* Filtros */}
            <Box sx={{ mb: 3 }} data-tour-id="pedidos-filtros">
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} lg={3}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por número, comprobante, nombre o CUIT..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <SearchableSelect
                    label="Estado"
                    allowEmpty
                    emptyOptionLabel="Todos"
                    value={filtroEstado || null}
                    onChange={(v) => { setFiltroEstado(v ?? ''); setPage(0); }}
                    options={PEDIDO_ESTADO_FILTER_OPTIONS}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    label="Fecha Desde"
                    type="date"
                    value={fechaDesde}
                    onChange={(e) => { setFechaDesde(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} lg={2}>
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => { setFechaHasta(e.target.value); setPage(0); }}
                    InputLabelProps={{ shrink: true }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: '#fafafa' } }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2}>
                  <FiltroSucursalVentaSelect
                    value={filtroNroSuc}
                    onChange={(v) => { setFiltroNroSuc(v); setPage(0); }}
                    loading={loadingSucursales}
                    sucursales={sucursales}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={2}>
                  <FormControlLabel
                    sx={{
                      m: 0,
                      height: '100%',
                      alignItems: 'center',
                      whiteSpace: { lg: 'nowrap' },
                    }}
                    control={
                      <Switch
                        size="small"
                        color="primary"
                        checked={filtroPendientesFacturar}
                        onChange={(e) => {
                          const newValue = e.target.checked;
                          setFiltroPendientesFacturar(newValue);
                          setCookie('pedidos_filtro_pendientes_facturar', newValue.toString());
                          setPage(0);
                        }}
                      />
                    }
                    label={
                      <>
                        <Box component="span" sx={{ display: { xs: 'inline', lg: 'none' } }}>
                          Pend. de Facturar
                        </Box>
                        <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
                          Pend. fact.
                        </Box>
                      </>
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Box data-tour-id="pedidos-kpis">
              <PedidosKpisBar
                cantidadPedidos={kpis.cantidadPedidos || totalRecords}
                precioTotal={kpis.precioTotal}
                precioNeto={kpis.precioNeto}
                pesoTotalKg={kpis.pesoTotalKg}
                volumenTotalM3={kpis.volumenTotalM3}
              />
            </Box>

            {/* Vista Mobile */}
            {isMobile ? (
              <Box data-tour-id="pedidos-grilla">
                {displayItems.map((pedido) => {
                  return (
                    <Card
                      key={pedido.nroFormulario}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        border: '1px solid #e0e0e0',
                        '&:hover': { boxShadow: '0 4px 8px rgba(0,0,0,0.1)' },
                      }}
                    >
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                                component="span"
                                variant="subtitle1"
                                sx={{ ...comprobanteSx, fontWeight: 600 }}
                                onClick={() => handleComprobanteClick(pedido)}
                              >
                                {formatComprobante(pedido) !== '-' ? formatComprobante(pedido) : 'Pedido'}
                              </Typography>
                            {renderEstadoChip(pedido)}
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            Fecha: {new Date(pedido.fechaEmision).toLocaleDateString()}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Vencimiento: {formatFechaVencimDisplay(pedido.fechaVencim ?? pedido.fechaEmision)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Cliente: {formatClienteConNro(pedido.nroCliente, pedido.nombre)}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            Importe Total: {formatCurrency(calcularTotal(pedido))}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ mt: 1 }}
                            {...(pedido.nroFormulario === displayItems[0]?.nroFormulario
                              ? { 'data-tour-id': 'pedidos-acciones' }
                              : {})}
                          >
                            {puedeImprimir(pedido) && (
                              <Can permiso="ventas.pedidos.imprimir">
                              <Tooltip title="Imprimir PDF">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleImprimirPDF(pedido.nroFormulario, e)}
                                  disabled={pdfLoading === pedido.nroFormulario}
                                  sx={btnSx('#2196f3', '#1976d2')}
                                >
                                  {pdfLoading === pedido.nroFormulario
                                    ? <CircularProgress size={16} sx={{ color: '#fff' }} />
                                    : <PrintIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                              </Can>
                            )}
                            {puedeFacturar(pedido) && permiteAcciones(pedido) && (
                              <Can permiso="ventas.facturas.nuevo">
                              <Tooltip title="Facturar pedido">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleFacturar(pedido.nroFormulario, e)}
                                  disabled={estadosActualizando.has(pedido.nroFormulario)}
                                  sx={btnSx('#4caf50', '#45a049')}
                                >
                                  <ReceiptIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              </Can>
                            )}
                            {permiteAcciones(pedido) && (
                              <Tooltip title="Más acciones">
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleKebabOpen(e, pedido.nroFormulario)}
                                  disabled={estadosActualizando.has(pedido.nroFormulario)}
                                  sx={{ borderRadius: 1.5, width: 32, height: 32 }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
                <TablePagination
                  rowsPerPageOptions={[5, 10, 20]}
                  component="div"
                  count={displayTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Filas por página"
                />
              </Box>
            ) : (
              /* Vista Desktop */
              <TableContainer
                component={Paper}
                sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e0e0e0' }}
                data-tour-id="pedidos-grilla"
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      {columnasTabla.map((col) => (
                        <TableCell key={col.id} align={col.align} sx={withColumnSx({ fontWeight: 600 }, col.sx)}>
                          {col.id === 'acciones' ? (
                            <AccionesColumnHeader label={col.label} onCustomize={(e) => setColumnasAnchorEl(e.currentTarget)} />
                          ) : (
                            col.label
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayItems.map((pedido) => (
                        <TableRow
                          key={pedido.nroFormulario}
                          hover
                          sx={{ '&:hover': { bgcolor: '#f9f9f9' } }}
                        >
                          {columnasTabla.map((col) => renderCeldaPedido(col.id, pedido, col.sx))}
                        </TableRow>
                    ))}
                  </TableBody>
                </Table>
                <TablePagination
                  rowsPerPageOptions={[5, 10, 20]}
                  component="div"
                  count={displayTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  labelRowsPerPage="Filas por página"
                  sx={{ borderTop: '1px solid #e0e0e0' }}
                />
              </TableContainer>
            )}
          </CardContent>
        </Card>

        {/* Menú kebab compartido */}
        <Menu
          anchorEl={kebabAnchor?.el}
          open={Boolean(kebabAnchor)}
          onClose={handleKebabClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {currentKebabPedido && renderKebabItems(currentKebabPedido)}
        </Menu>

        <PedidoDetalle
          open={detalleDialogOpen}
          onClose={handleCloseDetalle}
          pedidoId={selectedPedidoId}
          onConversionDisparada={() => {
            if (selectedPedidoId != null) {
              setCantidadBPorPedido((prev) => {
                const next = { ...prev };
                delete next[selectedPedidoId];
                return next;
              });
            }
            refreshListado();
          }}
        />

        <CopiarPedidoRelacionadoDialog
          open={enviarPedidoId != null && enviarPedidoId > 0}
          pedidoIds={enviarPedidoId != null && enviarPedidoId > 0 ? [enviarPedidoId] : []}
          onClose={() => setEnviarPedidoId(null)}
          onCopied={() => {
            if (enviarPedidoId != null) {
              setCantidadBPorPedido((prev) => ({ ...prev, [enviarPedidoId]: false }));
            }
            setEnviarPedidoId(null);
            refreshListado();
          }}
          title="Enviar pedido"
          description="Se envía la Cantidad B a las empresas que elijas. El pedido origen conserva el remanente."
          confirmLabel="Enviar pedido"
        />

        {pdfUrl && selectedPedido && (
          <PDFViewer
            pdfUrl={pdfUrl}
            onClose={handleClosePDF}
            title={`Pedido #${selectedPedido}`}
            documentId={`${selectedPedido}`}
            documentType="pedido"
          />
        )}

        <PersonalizarColumnasDialog
          anchorEl={columnasAnchorEl}
          onClose={() => setColumnasAnchorEl(null)}
          onReorder={reordenarColumnas}
          columns={columnasDefinidas}
          columnOrder={gridColumns.columnOrder}
          visibleIds={gridColumns.visibleIds}
          onToggle={toggleColumnaVisible}
          onMove={moverColumna}
          onReset={restablecerColumnas}
          addableColumns={columnasParaAgregar}
          onAdd={agregarColumna}
        />
      </Box>
  );
};

export default Pedidos;
